import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { ArrowRight, Wrench, CheckCircle } from 'lucide-react';
import axios from 'axios';
import Layout from '../components/Layout';
import OptimizedImage from '../components/OptimizedImage';
import { useSchemaOrg } from '../hooks/useSchemaOrg';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const ServicesPage = () => {
  const [servicePages, setServicePages] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const navigate = useNavigate();
  const { name: companyName, organization, SITE_URL } = useSchemaOrg();

  useEffect(() => {
    Promise.all([fetchServicePages(), fetchServices()]).finally(() => setLoading(false));
  }, []);

  const fetchServicePages = async () => {
    try {
      const response = await axios.get(`${API}/pages?page_type=service_page&published_only=true`);
      setServicePages(response.data);
    } catch (error) {
      // Service pages may not exist yet
    }
  };

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data.filter(s => s.active !== false));
    } catch (error) {
      // Fallback
    }
  };

  const categories = ['all', ...new Set(services.map(s => s.category).filter(Boolean))];
  const filteredServices = selectedCategory === 'all'
    ? services
    : services.filter(s => s.category === selectedCategory);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": `Tjenester fra ${companyName}`,
    "description": "Komplett oversikt over våre verkstedtjenester",
    "itemListElement": services.map((service, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Service",
        "name": service.title || service.name,
        "description": service.description,
        "provider": organization,
        "offers": service.price ? {
          "@type": "Offer",
          "price": service.price,
          "priceCurrency": "NOK"
        } : undefined
      }
    }))
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-black flex items-center justify-center">
          <div className="text-white text-xl">Laster tjenester...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Helmet>
        <title>Våre tjenester | {companyName}</title>
        <meta name="description" content="Se alle våre verkstedtjenester - EU-kontroll for tungbil, tilhenger og personbil, service, reparasjoner og mer." />
        <link rel="canonical" href={`${SITE_URL}/tjenester`} />
        <meta property="og:title" content={`Våre tjenester | ${companyName}`} />
        <meta property="og:description" content="Profesjonell EU-kontroll og verkstedtjenester for tungbil, tilhenger og personbil." />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-black to-gray-900">
        {/* Hero Section */}
        <div className="relative pt-32 pb-16">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(239,68,68,0.1),transparent_50%)]" />
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="bg-red-600/20 text-red-400 border-red-600/50 mb-6 px-4 py-2" data-testid="services-badge">
                <Wrench className="w-4 h-4 inline mr-2" />
                Verkstedtjenester
              </Badge>
              <h1 className="text-4xl sm:text-5xl font-bold mb-6">
                <span className="text-white">Våre </span>
                <span className="bg-gradient-to-r from-red-500 to-yellow-400 bg-clip-text text-transparent">
                  tjenester
                </span>
              </h1>
              <p className="text-xl text-gray-300">
                Profesjonell EU-kontroll og verkstedtjenester for tungbil, tilhenger og personbil.
                Vi har kapasitet for opptil 6 store tilhengere samtidig.
              </p>
            </div>
          </div>
        </div>

        {/* Service Pages (rich cards with images) */}
        {servicePages.length > 0 && (
          <div className="container mx-auto px-4 pb-16" data-testid="service-pages-grid">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Utforsk våre tjenester</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
              {servicePages.map((page) => (
                <Card
                  key={page.id}
                  onClick={() => navigate(`/tjeneste/${page.slug}`)}
                  className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border-gray-700 hover:border-red-600/50 cursor-pointer transition-all duration-300 hover:scale-[1.03] hover:shadow-2xl hover:shadow-red-500/20 group"
                  data-testid={`service-page-card-${page.slug}`}
                >
                  {page.featured_image && (
                    <div className="w-full h-48 overflow-hidden rounded-t-lg">
                      <OptimizedImage
                        src={page.featured_image}
                        alt={page.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                    </div>
                  )}
                  <CardHeader>
                    <CardTitle className="text-white group-hover:text-red-400 transition-colors line-clamp-2">
                      {page.title}
                    </CardTitle>
                    {page.excerpt && (
                      <CardDescription className="text-gray-400 line-clamp-3 mt-2">
                        {page.excerpt}
                      </CardDescription>
                    )}
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center text-red-400 group-hover:text-red-300 font-semibold">
                      <span>Les mer</span>
                      <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-2 transition-transform" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Pricing Services */}
        {services.length > 0 && (
          <div className="container mx-auto px-4 pb-20" data-testid="services-pricing">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Prisliste</h2>

            {/* Category Filter */}
            {categories.length > 2 && (
              <div className="flex flex-wrap gap-2 justify-center mb-10">
                {categories.map(cat => (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      selectedCategory === cat
                        ? 'bg-red-600 text-white'
                        : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    }`}
                    data-testid={`category-filter-${cat}`}
                  >
                    {cat === 'all' ? 'Alle tjenester' : cat}
                  </button>
                ))}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
              {filteredServices.map((service) => (
                <article
                  key={service.id}
                  className="group bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-gray-700 rounded-xl overflow-hidden hover:border-red-600/50 transition-all duration-300"
                  data-testid={`service-card-${service.id}`}
                >
                  <div className="p-6">
                    {service.category && (
                      <span className="inline-block px-3 py-1 text-xs font-medium bg-red-600/20 text-red-400 rounded-full mb-3">
                        {service.category}
                      </span>
                    )}
                    <h3 className="text-xl font-bold text-white mb-3 group-hover:text-red-400 transition-colors">
                      {service.title || service.name}
                    </h3>
                    {service.description && (
                      <p className="text-gray-400 text-sm mb-4 line-clamp-3">{service.description}</p>
                    )}
                    {service.features && service.features.length > 0 && (
                      <ul className="space-y-1 mb-4">
                        {service.features.slice(0, 3).map((feature, idx) => (
                          <li key={idx} className="flex items-center text-sm text-gray-300">
                            <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                            <span className="truncate">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-700">
                      {service.price ? (
                        <div className="text-2xl font-bold text-white">{service.price}</div>
                      ) : (
                        <div className="text-gray-500 text-sm">Kontakt for pris</div>
                      )}
                      <a
                        href="/kontakt"
                        className="flex items-center text-red-400 hover:text-red-300 font-medium text-sm"
                      >
                        Bestill
                        <ArrowRight className="w-4 h-4 ml-1" />
                      </a>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {filteredServices.length === 0 && (
              <div className="text-center py-20">
                <Wrench className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 text-lg">Ingen tjenester funnet i denne kategorien</p>
              </div>
            )}
          </div>
        )}

        {/* CTA Section */}
        <div className="container mx-auto px-4 pb-20">
          <div className="bg-gradient-to-r from-red-900/30 to-yellow-900/30 border border-red-600/30 rounded-2xl p-8 md:p-12 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Trenger du hjelp med å velge riktig tjeneste?
            </h2>
            <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
              Vi hjelper deg gjerne med å finne riktig løsning for ditt kjøretøy.
              Kontakt oss for en uforpliktende prat.
            </p>
            <a
              href="/kontakt"
              className="inline-flex items-center px-8 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold rounded-lg transition-all"
            >
              Kontakt oss
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ServicesPage;
